import { reactive, watch, nextTick } from "vue";
import ProfilsConfig from "@/data/profils.js";

const PROFIL_ORDER = ["visualImpairments", "colors", "cognitiveImpairments", "dyslexic", "cursor", "screenReader", "simpleKeyboard"];

export function useProfileSystem({
    zoom,
    sectionContentRef,
    modalProfilInputRef,
    cursor,
    magnifier,
    screenRuler,
    screenReader,
    dyslexicFont,
    moduleUI,
    isDarkModeRef,
    refreshSnapshot,
}) {
    const state = reactive({
        profils: PROFIL_ORDER.map(name => ({
            name,
            type: ProfilsConfig[name]?.type ?? name,
            icon: ProfilsConfig[name]?.icon ?? "",
            isChecked: false,
        })),
        profilFilters: [],
        filtersActive: [],
        profilContent: { active: false, isFontForDyslexic: false },
        newProfilName: "",
        isProfilModalActive: false,
        userProfils: [],
        isColorsSettingsMenuOpen: false,
    });

    watch(() => state.filtersActive, (filters) => {
        const html = document.querySelector("html");
        html.style.filter = filters.length
            ? filters.map(f => `${f.filterType}(${f.value}%)`).join(" ")
            : "";
    }, { deep: true });

    function resetProfil(preserveMagnifier = false) {
        state.profilFilters = [];
        state.filtersActive = [];
        state.profilContent = { active: false, isFontForDyslexic: false };
        zoom.reset();
        moduleUI.state.visualProfileZoomValue = 100;
        moduleUI.state.isVisualZoomMenuOpen = false;
        screenRuler.reset();
        screenReader.state.isScreenReaderActive = false;
        cursor.reset();
        dyslexicFont.setDyslexicFontState(false);
        magnifier.state.isMagnifierSettingsMenuOpen = false;
        magnifier.state.isMagnifierFrameVisible = true;
        magnifier.state.magnifierPinnedX = window.innerWidth / 2;
        magnifier.state.magnifierPinnedY = window.innerHeight / 2;
        magnifier.state.magnifierPinningEnabled = false;
        magnifier.state.isMagnifierDragging = false;
        magnifier.state.magnifierMode = "cursor";
        magnifier.state.magnifierFixedX = window.innerWidth / 2;
        magnifier.state.magnifierFixedY = window.innerHeight / 2;
        state.isColorsSettingsMenuOpen = false;
        if (!preserveMagnifier) magnifier.state.isMagnifierActive = false;
        if (isDarkModeRef) isDarkModeRef.value = false;
        refreshSnapshot(200);
    }

    function applyZoomFromProfile(value) {
        if (!value) return;
        zoom.applyProfileZoom(value);
    }

    function getRuntimeZoomSnapshot() {
        return {
            isZoomActive: zoom.state.isZoomActive,
            zoomValue: zoom.state.zoomValue,
            isModuleZoomActive: zoom.state.isModuleZoomActive,
            moduleZoomValue: zoom.state.moduleZoomValue,
            isTextZoomActive: zoom.state.isTextZoomActive,
            textZoomValue: zoom.state.textZoomValue,
        };
    }

    function reapplyRuntimeZoomSnapshot(snapshot) {
        if (snapshot.isZoomActive) zoom.applyProfileZoom(snapshot.zoomValue);
        if (snapshot.isModuleZoomActive) zoom.applyProfileModuleZoom(snapshot.moduleZoomValue);
        if (snapshot.isTextZoomActive) zoom.applyProfileTextZoom(snapshot.textZoomValue);
    }

    function activeProfil(profilOrigin, profilName = null, profilIndex = null) {
        let profil;
        let profileHasCustomCursorColors = false;
        if (profilOrigin === "app") profil = ProfilsConfig[profilName];
        if (profilOrigin === "user") profil = state.userProfils[profilIndex];
        if (!profil) return;

        for (const type in profil) {
            switch (type) {
                case "zoomValue":
                    applyZoomFromProfile(profil[type]);
                    break;
                case "moduleZoomValue":
                    if (profil[type]) zoom.applyProfileModuleZoom(profil[type]);
                    break;
                case "textZoomValue":
                    if (profil[type]) zoom.applyProfileTextZoom(profil[type]);
                    break;
                case "isFontActive":
                    if (profil[type]) {
                        state.profilContent.active = true;
                        state.profilContent.isFontForDyslexic = true;
                        dyslexicFont.setDyslexicFontState(true);
                    }
                    break;
                case "isCursorActive":
                    if (profil[type]) cursor.state.isCursorActive = profil[type];
                    break;
                case "cursorColor":
                    if (profil[type]) { cursor.state.normalCursorColor = profil[type]; profileHasCustomCursorColors = true; }
                    break;
                case "hoverCursorColor":
                    if (profil[type]) { cursor.state.hoverCursorColor = profil[type]; profileHasCustomCursorColors = true; }
                    break;
                case "cursorScale":
                    if (profil[type]) cursor.state.cursorScale = profil[type];
                    break;
                case "isMagnifierActive":
                    magnifier.state.isMagnifierActive = !!profil[type];
                    break;
                case "magnifierZoom":
                    if (profil[type]) {
                        const mag = magnifier.magnifierViewRef?.value;
                        magnifier.state.magnifierZoom = mag ? mag.normalizeZoom(profil[type]) : profil[type];
                    }
                    break;
                case "magnifierSize":
                    if (profil[type]) {
                        const mag = magnifier.magnifierViewRef?.value;
                        magnifier.state.magnifierSize = mag ? mag.normalizeSize(profil[type]) : profil[type];
                    }
                    break;
                case "isMagnifierFrameVisible":
                    magnifier.state.isMagnifierFrameVisible = !!profil[type];
                    break;
                case "magnifierMode":
                    magnifier.setMagnifierMode(profil[type]);
                    break;
                case "magnifierFixedX":
                    if (typeof profil[type] === "number") magnifier.state.magnifierFixedX = magnifier.normalizeMagnifierFixedCoordinate(profil[type], "x");
                    break;
                case "magnifierFixedY":
                    if (typeof profil[type] === "number") magnifier.state.magnifierFixedY = magnifier.normalizeMagnifierFixedCoordinate(profil[type], "y");
                    break;
                case "isScreenRulerActive":
                    if (profil[type]) screenRuler.state.isScreenRulerActive = true;
                    break;
                case "isScreenReaderActive":
                    if (profil[type]) screenReader.state.isScreenReaderActive = true;
                    break;
                case "isDarkModeActive":
                    if (isDarkModeRef) isDarkModeRef.value = !!profil[type];
                    break;
                case "screenRulerColor":
                    if (profil[type]) screenRuler.state.screenRulerColor = profil[type];
                    break;
                case "screenRulerHeight":
                    if (profil[type] != null) screenRuler.state.screenRulerHeight = profil[type];
                    break;
                case "filtersActive":
                    if (Array.isArray(profil[type]) && profil[type].length > 0) {
                        state.profilFilters = profil[type].map(f => ({ filterType: f.filterType, value: f.value }));
                        state.filtersActive = profil[type].map(f => ({ filterType: f.filterType, value: f.value }));
                    }
                    break;
                default:
                    break;
            }
        }

        if (profileHasCustomCursorColors) {
            cursor.state.hasCustomCursorColors = true;
            cursor.state.cursorColor = cursor.state.normalCursorColor;
        }

        refreshSnapshot(200);
    }

    function applyActiveProfil() {
        state.profils.forEach(profil => {
            if (profil.isChecked) activeProfil("app", profil.name);
        });
        state.userProfils.forEach((profil, index) => {
            if (profil.isProfilActive) activeProfil("user", null, index);
        });
    }

    function toggleProfil(index, profilName) {
        state.profils[index].isChecked = !state.profils[index].isChecked;
        const isNowChecked = state.profils[index].isChecked;
        const runtimeZoomSnapshot = profilName !== "visualImpairments" ? getRuntimeZoomSnapshot() : null;

        if (profilName === "visualImpairments" && isNowChecked) {
            moduleUI.state.isZoomExpanded = true;
        }

        const shouldOpenColorsMenu = profilName === "colors" && isNowChecked;
        const shouldOpenScreenRulerMenu = profilName === "cognitiveImpairments" && isNowChecked;

        resetProfil(true);
        applyActiveProfil();
        if (shouldOpenColorsMenu) {
            state.isColorsSettingsMenuOpen = true;
        }
        if (shouldOpenScreenRulerMenu) {
            screenRuler.state.isScreenRulerSettingsMenuOpen = true;
        }
        nextTick(() => {
            applyActiveProfil();
            reapplyRuntimeZoomSnapshot(runtimeZoomSnapshot);
            if (shouldOpenColorsMenu) {
                state.isColorsSettingsMenuOpen = true;
            }
            if (shouldOpenScreenRulerMenu) {
                screenRuler.state.isScreenRulerSettingsMenuOpen = true;
            }
            refreshSnapshot(200);
        });
    }

    function toggleColorsSettingsMenu() {
        state.isColorsSettingsMenuOpen = !state.isColorsSettingsMenuOpen;
        refreshSnapshot(380);
    }

    function handleOpenProfilModal() {
        state.isProfilModalActive = true;
    }

    function handleCloseProfilModal() {
        state.isProfilModalActive = false;
    }

    function handleCreateUserProfil() {
        handleCloseProfilModal();
        const filtersData = state.filtersActive.map(f => ({ filterType: f.filterType, value: f.value }));
        const newProfilValues = {
            isProfilActive: true,
            name: state.newProfilName,
            isCursorActive: cursor.state.isCursorActive,
            cursorColor: cursor.state.normalCursorColor,
            cursorScale: cursor.state.cursorScale,
            hoverCursorColor: cursor.state.hoverCursorColor,
            isMagnifierActive: magnifier.state.isMagnifierActive,
            magnifierZoom: magnifier.state.magnifierZoom,
            magnifierSize: magnifier.state.magnifierSize,
            isMagnifierFrameVisible: magnifier.state.isMagnifierFrameVisible,
            magnifierMode: magnifier.state.magnifierMode,
            magnifierFixedX: magnifier.state.magnifierFixedX,
            magnifierFixedY: magnifier.state.magnifierFixedY,
            isScreenRulerActive: screenRuler.state.isScreenRulerActive,
            isScreenReaderActive: screenReader.state.isScreenReaderActive,
            isDarkModeActive: isDarkModeRef?.value ?? false,
            screenRulerColor: screenRuler.state.screenRulerColor,
            screenRulerHeight: screenRuler.state.screenRulerHeight,
            isFontActive: dyslexicFont.state.isDyslexicFontActive,
            zoomValue: zoom.state.isZoomActive ? zoom.state.zoomValue : null,
            moduleZoomValue: zoom.state.isModuleZoomActive ? zoom.state.moduleZoomValue : null,
            textZoomValue: zoom.state.isTextZoomActive ? zoom.state.textZoomValue : null,
            filtersActive: filtersData,
        };
        const newUserProfil = JSON.stringify([...state.userProfils, newProfilValues]);
        window.localStorage.setItem("tenjityModuleProfils", newUserProfil);
        state.newProfilName = "";
        state.userProfils = JSON.parse(newUserProfil);
    }

    function toggleUserProfil(index) {
        state.userProfils[index].isProfilActive = !state.userProfils[index].isProfilActive;
        window.localStorage.setItem("tenjityModuleProfils", JSON.stringify(state.userProfils));
        resetProfil(true);
        applyActiveProfil();
        nextTick(() => {
            moduleUI.resetContentScroll();
            applyActiveProfil();
            refreshSnapshot(200);
        });
    }

    function deleteUserProfil(index) {
        state.userProfils.splice(index, 1);
        resetProfil();
        applyActiveProfil();
        window.localStorage.setItem("tenjityModuleProfils", JSON.stringify(state.userProfils));
    }

    watch(() => state.isProfilModalActive, (val) => {
        if (val && modalProfilInputRef.value) {
            nextTick(() => modalProfilInputRef.value.focus());
        }
    });

    function updateFiltersActive(filters) {
        state.filtersActive = filters;
    }

    return {
        state,
        resetProfil,
        activeProfil,
        applyActiveProfil,
        toggleProfil,
        handleOpenProfilModal,
        handleCloseProfilModal,
        handleCreateUserProfil,
        toggleUserProfil,
        deleteUserProfil,
        toggleColorsSettingsMenu,
        updateFiltersActive,
    };
}
